import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { XMarkIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { FilterGroup } from '../types'
import toast from 'react-hot-toast'

interface SaveListModalProps {
  datasetId: string
  filter: FilterGroup | null
  rowCount: number
  onClose: () => void
}

export default function SaveListModal({ datasetId, filter, rowCount, onClose }: SaveListModalProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [listType, setListType] = useState<'static' | 'dynamic'>('static')
  
  const createMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        dataset_id: datasetId,
        name,
        description: description || undefined,
        list_type: listType,
      }
      
      // For dynamic lists, include the filter definition
      if (listType === 'dynamic' && filter) {
        payload.filter_definition = filter
      }
      
      const res = await api.post('/lists', payload)
      
      // For static lists with a filter, we need to add the filtered rows
      if (listType === 'static' && filter) {
        // Get all filtered row IDs
        const rowsRes = await api.post<{ items: { id: string }[] }>(
          `/datasets/${datasetId}/rows/filter`,
          { filter },
          { params: { page: 1, page_size: 10000 } }
        )
        
        if (rowsRes.data.items.length > 0) {
          const rowIds = rowsRes.data.items.map(r => r.id)
          await api.post(`/lists/${res.data.id}/members`, { row_ids: rowIds })
        }
      }
      
      return res.data
    },
    onSuccess: () => {
      toast.success('List created successfully!')
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      onClose()
    },
    onError: () => {
      toast.error('Failed to create list')
    },
  })
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Please enter a list name')
      return
    }
    if (listType === 'dynamic' && !filter) {
      toast.error('Dynamic lists require a filter. Please add a filter first.')
      return
    }
    createMutation.mutate()
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Save as List</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              List Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="e.g., VIP Customers"
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Optional description..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              List Type
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="listType"
                  value="static"
                  checked={listType === 'static'}
                  onChange={() => setListType('static')}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-medium text-gray-900">Static List</div>
                  <div className="text-sm text-gray-500">
                    {filter 
                      ? `Snapshot of ${rowCount.toLocaleString()} current rows matching your filter`
                      : `Add all ${rowCount.toLocaleString()} rows to this list`
                    }
                  </div>
                </div>
              </label>
              
              <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer ${!filter ? 'opacity-50' : 'hover:bg-gray-50'}`}>
                <input
                  type="radio"
                  name="listType"
                  value="dynamic"
                  checked={listType === 'dynamic'}
                  onChange={() => setListType('dynamic')}
                  disabled={!filter}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-medium text-gray-900">Dynamic List</div>
                  <div className="text-sm text-gray-500">
                    {filter 
                      ? 'Auto-updates when new rows match this filter'
                      : 'Add a filter first to create a dynamic list'
                    }
                  </div>
                </div>
              </label>
            </div>
          </div>
          
          {filter && (filter.and || filter.or) && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="text-sm font-medium text-blue-800 mb-2">
                Filter Applied ({filter.and ? 'AND' : 'OR'}) - {rowCount.toLocaleString()} rows match
              </div>
              <ul className="text-sm text-blue-700 space-y-1">
                {(filter.and || filter.or || []).map((cond, idx) => {
                  if ('column' in cond) {
                    return (
                      <li key={idx} className="flex items-center gap-1 flex-wrap">
                        <span className="font-medium">{cond.column}</span>
                        <span className="text-blue-500">{cond.op}</span>
                        {cond.value !== undefined && (
                          <span className="bg-blue-100 px-1.5 py-0.5 rounded">
                            {String(cond.value)}
                          </span>
                        )}
                      </li>
                    )
                  }
                  return <li key={idx} className="text-xs text-blue-500">(nested group)</li>
                })}
              </ul>
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex-1 btn-primary"
            >
              {createMutation.isPending ? 'Creating...' : 'Create List'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
